'use client';

import {
  Box,
  FormControl,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import type { Connector } from '@repo/api-gw-sdk';
import { useEffect, useState } from 'react';

import { defaultAddConnectorWizardState } from '@/app/(dashboard)/lakehouse/connectors/wizard/addConnectorWizardState';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';

import type { AddCatalogWizardState } from '../addCatalogWizardState';

const CATALOG_TYPES = [
  {
    id: 'GLUE_CATALOG' as const,
    label: 'AWS Glue',
    image: '/statics/images/lakehouse/Glue.png',
    connectorType: 'AWS_ACCOUNT_CONNECTOR',
  },
  {
    id: 'BIGLAKE_CATALOG' as const,
    label: 'Google BigLake',
    image: '/statics/images/lakehouse/BigLake.png',
    connectorType: 'GCP_PROJECT_CONNECTOR',
  },
  {
    id: 'ICEBERG_REST_CATALOG' as const,
    label: 'Iceberg REST',
    image: '/statics/images/lakehouse/Iceberg.png',
    connectorType: 'AWS_ACCOUNT_CONNECTOR',
  },
];

export const CatalogConnectorTypeStep = (
  props: StepProps<AddCatalogWizardState>
) => {
  const dal = useDAL();
  const { workspaceId, catalogType } = props.wizardState;
  const [connectors, setConnectors] = useState<Connector[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [connectorMode, setConnectorMode] = useState<'existing' | 'new'>(
    props.wizardState.connectorSource?.mode ?? 'existing'
  );
  const [selectedConnectorId, setSelectedConnectorId] = useState(
    props.wizardState.connectorSource?.mode === 'existing'
      ? props.wizardState.connectorSource.connectorId
      : ''
  );

  // Find the required connector type for the selected catalog type
  const requiredConnectorType = CATALOG_TYPES.find(
    (ct) => ct.id === catalogType
  )?.connectorType;

  // Filter connectors to only show those matching the required type
  const filteredConnectors = connectors.filter(
    (c) => !requiredConnectorType || c.connectorType === requiredConnectorType
  );

  useEffect(() => {
    if (!workspaceId || !catalogType) return;
    if (loaded) return;
    setLoading(true);
    dal.lakehouse
      .listConnectors(workspaceId)
      .then((resp) => {
        const active = (resp?.connectors ?? []).filter(
          (c: Connector) => c.status === 'ACTIVE'
        );
        setConnectors(active);
        setLoaded(true);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, catalogType]);

  // When catalog type changes, reset connector selection if filtered connectors are empty
  useEffect(() => {
    if (filteredConnectors.length === 0 && loaded) {
      setConnectorMode('new');
      setSelectedConnectorId('');
    }
  }, [catalogType, filteredConnectors.length, loaded]);

  const handleCatalogTypeClick = (
    type: 'GLUE_CATALOG' | 'BIGLAKE_CATALOG' | 'ICEBERG_REST_CATALOG'
  ) => {
    if (type !== catalogType) {
      setSelectedConnectorId('');
      setLoaded(false);
    }
    props.setWizardState({
      ...props.wizardState,
      catalogType: type,
    });
  };

  const canGoNext =
    catalogType !== null &&
    (connectorMode === 'new' ||
      (connectorMode === 'existing' && selectedConnectorId !== ''));

  const handleNext = () => {
    if (connectorMode === 'existing') {
      const connector = connectors.find((c) => c.id === selectedConnectorId);
      props.setWizardState({
        ...props.wizardState,
        connectorSource: {
          mode: 'existing',
          connectorId: selectedConnectorId,
        },
        connectorExternalId: connector?.connectorExternalId ?? '',
      });
      // next[0]: Glue existing → select region, next[3]: BigLake existing → biglake config, next[4]: Iceberg existing → iceberg config
      if (catalogType === 'BIGLAKE_CATALOG') {
        props.setNextStep(props.currentStep.next?.[3]);
      } else if (catalogType === 'ICEBERG_REST_CATALOG') {
        props.setNextStep(props.currentStep.next?.[4]);
      } else {
        props.setNextStep(props.currentStep.next?.[0]);
      }
    } else {
      const newConnectorType =
        catalogType === 'BIGLAKE_CATALOG'
          ? 'GCP_PROJECT_CONNECTOR'
          : 'AWS_ACCOUNT_CONNECTOR';
      props.setWizardState({
        ...props.wizardState,
        connectorSource: {
          mode: 'new',
          connectorState:
            props.wizardState.connectorSource?.mode === 'new'
              ? props.wizardState.connectorSource.connectorState
              : {
                  ...defaultAddConnectorWizardState,
                  connectorType: newConnectorType,
                },
        },
      });
      // next[1]: new AWS connector, next[2]: new GCP connector, next[5]: new AWS connector (Iceberg)
      if (catalogType === 'BIGLAKE_CATALOG') {
        props.setNextStep(props.currentStep.next?.[2]);
      } else if (catalogType === 'ICEBERG_REST_CATALOG') {
        props.setNextStep(props.currentStep.next?.[5]);
      } else {
        props.setNextStep(props.currentStep.next?.[1]);
      }
    }
  };

  return (
    <StepContainer
      stepperIndex={0}
      stepperLabels={props.stepperLabels}
      canGoNext={() => canGoNext}
      nextButtonText='Next'
      backButtonText='Cancel'
      onBackClick={props.back}
      onNextClick={async () => handleNext()}
    >
      <Stack gap='32px' padding='24px 40px'>
        <Stack gap='16px'>
          <Typography variant='subtitle2' fontWeight={700}>
            Catalog Type
          </Typography>
          <Stack direction='row' gap='24px'>
            {CATALOG_TYPES.map((ct) => {
              const selected = catalogType === ct.id;
              return (
                <Stack
                  key={ct.id}
                  alignItems='center'
                  gap='8px'
                  onClick={() => handleCatalogTypeClick(ct.id)}
                  sx={{
                    cursor: 'pointer',
                    opacity: selected ? 1 : 0.6,
                    '&:hover': { opacity: 1 },
                  }}
                >
                  <Box
                    component='img'
                    src={ct.image}
                    alt={ct.label}
                    sx={{
                      width: '160px',
                      height: '160px',
                      objectFit: 'contain',
                    }}
                  />
                  <Typography
                    variant='body2'
                    fontWeight={500}
                    color={selected ? 'text.primary' : 'text.secondary'}
                  >
                    {ct.label}
                  </Typography>
                </Stack>
              );
            })}
          </Stack>
        </Stack>

        {catalogType && (
          <Stack gap='16px'>
            <Typography variant='subtitle2' fontWeight={700}>
              Connector
            </Typography>

            {loading ? (
              <Typography variant='body2' color='text.secondary'>
                Loading connectors...
              </Typography>
            ) : (
              <>
                <RadioGroup
                  value={connectorMode}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === 'existing' || val === 'new') {
                      setConnectorMode(val);
                    }
                  }}
                >
                  <FormControlLabel
                    value='existing'
                    control={<Radio size='small' />}
                    label='Use existing connector'
                    disabled={filteredConnectors.length === 0}
                  />
                  <FormControlLabel
                    value='new'
                    control={<Radio size='small' />}
                    label='Create new connector'
                  />
                </RadioGroup>

                {connectorMode === 'existing' &&
                  filteredConnectors.length > 0 && (
                    <Stack gap='8px'>
                      <Typography variant='subtitle1'>
                        Select Connector
                      </Typography>
                      <FormControl size='small' sx={{ width: '30%' }}>
                        <Select
                          value={selectedConnectorId}
                          displayEmpty
                          onChange={(e) =>
                            setSelectedConnectorId(e.target.value)
                          }
                        >
                          <MenuItem value='' disabled>
                            Select a connector
                          </MenuItem>
                          {filteredConnectors.map((c) => (
                            <MenuItem key={c.id} value={c.id}>
                              {c.name} ({c.connectorExternalId})
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Stack>
                  )}

                {connectorMode === 'existing' &&
                  filteredConnectors.length === 0 && (
                    <Typography variant='body2' color='text.secondary'>
                      No active connectors found for this catalog type. Please
                      create a new connector.
                    </Typography>
                  )}
              </>
            )}
          </Stack>
        )}
      </Stack>
    </StepContainer>
  );
};
